import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import {
  boardContentsSchema,
  listListSchema,
  type BoardMember,
  type BoardContents,
  type Reconciliation,
} from '@molt/shared';
import { get } from './api';
import { boardMembersQueryOptions, workspaceBoardsQueryOptions } from './boards';
import { queryKeys, type MoltQueryKey } from './query-keys';
import { workspacesQueryOptions } from './workspaces';

export interface DestinationBoardOption {
  readonly id: string;
  readonly title: string;
  readonly workspaceId: string;
  readonly workspaceName: string;
}

export interface DestinationListOption {
  readonly id: string;
  readonly boardId: string;
  readonly title: string;
}

export type CardMovePlacement = 'top' | 'bottom' | `after:${string}`;

export function cardMoveNeighbours(
  cards: readonly { id: string }[],
  placement: CardMovePlacement,
): { previousCardId: string | null; nextCardId: string | null } {
  if (placement === 'top') {
    return { previousCardId: null, nextCardId: cards[0]?.id ?? null };
  }

  if (placement === 'bottom') {
    return { previousCardId: cards.at(-1)?.id ?? null, nextCardId: null };
  }

  const previousId = placement.slice('after:'.length);
  const previousIndex = cards.findIndex((card) => card.id === previousId);

  if (previousIndex === -1) {
    return { previousCardId: cards.at(-1)?.id ?? null, nextCardId: null };
  }

  return {
    previousCardId: previousId,
    nextCardId: cards[previousIndex + 1]?.id ?? null,
  };
}

export function canUseBoardAsMoveDestination(
  members: readonly Pick<BoardMember, 'id' | 'role'>[] | undefined,
  currentUserId: string | undefined,
): boolean {
  if (members === undefined || currentUserId === undefined) return false;
  const currentMember = members.find((member) => member.id === currentUserId);
  return currentMember?.role === 'admin' || currentMember?.role === 'normal';
}

export function boardContentsKeysInCache(
  queryClient: QueryClient,
  boardId: string,
): MoltQueryKey[] {
  return queryClient
    .getQueryCache()
    .findAll({ queryKey: queryKeys.boards.contents(boardId) })
    .map((query) => query.queryKey);
}

export function setBoardContentsQueries(
  queryClient: QueryClient,
  boardId: string,
  updater: (previous: BoardContents | undefined) => BoardContents | undefined,
): void {
  queryClient.setQueriesData<BoardContents>(
    { queryKey: queryKeys.boards.contents(boardId) },
    updater,
  );
}

export function removeCardFromBoardContents(
  previous: BoardContents | undefined,
  cardId: string,
): BoardContents | undefined {
  if (previous === undefined) return undefined;

  let removed = false;
  const lists = previous.lists.map((list) => {
    const cards = list.cards.filter((card) => card.id !== cardId);
    if (cards.length === list.cards.length) return list;
    removed = true;
    return {
      ...list,
      cards,
      cardCount: Math.max(0, list.cardCount - 1),
      openCardCount: Math.max(0, list.openCardCount - 1),
    };
  });

  return removed ? { ...previous, lists } : previous;
}

export function removeListFromBoardContents(
  previous: BoardContents | undefined,
  listId: string,
): BoardContents | undefined {
  if (previous === undefined) return undefined;

  const lists = previous.lists.filter((list) => list.id !== listId);
  return lists.length === previous.lists.length ? previous : { ...previous, lists };
}

export function boardMoveInvalidationKeys(
  sourceBoardId: string,
  destinationBoardId: string,
): MoltQueryKey[] {
  return [
    queryKeys.boards.contents(sourceBoardId),
    queryKeys.boards.contents(destinationBoardId),
    queryKeys.boards.lists(sourceBoardId),
    queryKeys.boards.lists(destinationBoardId),
    queryKeys.boards.labels(destinationBoardId),
  ];
}

export function hasReconciliationConsequences(reconciliation: Reconciliation): boolean {
  return (
    reconciliation.labels.created.length > 0 ||
    reconciliation.labels.dropped.length > 0 ||
    reconciliation.members.removed.length > 0 ||
    reconciliation.customFields.created.length > 0 ||
    reconciliation.customFields.optionsCreated.length > 0 ||
    reconciliation.customFields.notAppliedToExisting.length > 0
  );
}

export function cardMovePositionChoices<T extends { id: string }>(
  cards: readonly T[],
  movingCardId?: string,
): {
  readonly previousCardId: string | null;
  readonly nextCardId: string | null;
}[] {
  const visibleCards =
    movingCardId === undefined ? cards : cards.filter((card) => card.id !== movingCardId);

  return Array.from({ length: visibleCards.length + 1 }, (_slot, index) => ({
    previousCardId: index === 0 ? null : visibleCards[index - 1]!.id,
    nextCardId: index === visibleCards.length ? null : visibleCards[index]!.id,
  }));
}

function moveBoardContentsQueryOptions(boardId: string) {
  return {
    queryKey: queryKeys.boards.contents(boardId),
    queryFn: () =>
      get(`/boards/${encodeURIComponent(boardId)}/contents`, boardContentsSchema),
  };
}

function moveBoardListsQueryOptions(boardId: string) {
  return {
    queryKey: queryKeys.boards.lists(boardId),
    queryFn: () =>
      get(`/boards/${encodeURIComponent(boardId)}/lists?state=open`, listListSchema),
  };
}

export function useMoveDestinations(
  enabled: boolean,
  currentUserId: string | undefined,
): {
  boards: readonly DestinationBoardOption[];
  listsByBoardId: ReadonlyMap<string, readonly DestinationListOption[]>;
  cardsByListId: ReadonlyMap<string, readonly { id: string; title: string }[]>;
  loading: boolean;
} {
  const workspaces = useQuery({ ...workspacesQueryOptions(), enabled });
  const workspaceBoards = useQueries({
    queries:
      enabled && workspaces.data !== undefined
        ? workspaces.data.map((workspace) => ({
            ...workspaceBoardsQueryOptions(workspace.id, 'open'),
            enabled,
          }))
        : [],
  });

  const candidates = useMemo(() => {
    return workspaceBoards.flatMap((query, index) => {
      const workspace = workspaces.data?.[index];
      if (workspace === undefined) return [];
      return (query.data ?? []).map((board) => ({
        id: board.id,
        title: board.title,
        workspaceId: workspace.id,
        workspaceName: workspace.name,
      }));
    });
  }, [workspaceBoards, workspaces.data]);

  const memberQueries = useQueries({
    queries:
      enabled && currentUserId !== undefined
        ? candidates.map((board) => ({ ...boardMembersQueryOptions(board.id), enabled }))
        : [],
  });

  const boards = useMemo(() => {
    if (currentUserId === undefined) return [];
    return candidates.filter((board, index) =>
      canUseBoardAsMoveDestination(memberQueries[index]?.data, currentUserId),
    );
  }, [candidates, currentUserId, memberQueries]);

  const listQueries = useQueries({
    queries: enabled
      ? boards.map((board) => ({ ...moveBoardListsQueryOptions(board.id), enabled }))
      : [],
  });
  const contentQueries = useQueries({
    queries: enabled
      ? boards.map((board) => ({ ...moveBoardContentsQueryOptions(board.id), enabled }))
      : [],
  });

  const listsByBoardId = useMemo(() => {
    const map = new Map<string, DestinationListOption[]>();
    for (const [index, board] of boards.entries()) {
      map.set(
        board.id,
        (listQueries[index]?.data ?? []).map((list) => ({
          id: list.id,
          boardId: list.boardId,
          title: list.title,
        })),
      );
    }
    return map;
  }, [boards, listQueries]);

  const cardsByListId = useMemo(() => {
    const map = new Map<string, readonly { id: string; title: string }[]>();
    for (const query of contentQueries) {
      for (const list of query.data?.lists ?? []) {
        map.set(
          list.id,
          list.cards.map((card) => ({ id: card.id, title: card.title })),
        );
      }
    }
    return map;
  }, [contentQueries]);

  const loading =
    enabled &&
    (workspaces.isPending ||
      workspaceBoards.some((query) => query.isPending) ||
      memberQueries.some((query) => query.isPending) ||
      listQueries.some((query) => query.isPending) ||
      contentQueries.some((query) => query.isPending));

  return { boards, listsByBoardId, cardsByListId, loading };
}
